import type { ReactNode } from "react";

import {
  Alert,
  Box,
  Button,
  Flex,
  Text,
  useMediaQuery
} from "@chakra-ui/react";
import { Icon, FeedbackIcons } from "@euka-mfes/bark";

interface MessageBoxProps {
  type: "error" | "success" | "warning" | "info";
  title?: string | ReactNode;
  children: ReactNode;
  actionBtnText?: string | ReactNode;
  alertTrackingID?: string;
  rest?: React.HTMLAttributes<HTMLElement>;
  actionButtonAction?: () => void;
  backgroundColor?: string;
  alertBorderColor?: string;
  iconColor?: string;
  iconPlacement?: "start" | "center";
  isBorderLess?: boolean;
  textColor?: string;
}

export const messageIcons = {
  success: () => (
    <Icon icon={<FeedbackIcons.Tick />} fillColor="success.800" size={24} />
  ),
  error: () => (
    <Icon icon={<FeedbackIcons.Error />} fillColor="danger.500" size={24} />
  ),
  warning: () => (
    <Icon icon={<FeedbackIcons.Warning />} fillColor="warning.600" size={24} />
  ),
  info: () => (
    <Icon icon={<FeedbackIcons.Info />} fillColor="action.600" size={24} />
  )
};

const MessageBox = ({
  type,
  title,
  children,
  actionBtnText,
  alertTrackingID,
  backgroundColor,
  alertBorderColor,
  actionButtonAction,
  isBorderLess = false,
  textColor,
  ...rest
}: MessageBoxProps) => {
  const [isLargerThanMD] = useMediaQuery(["(min-width: 48em)"], {
    fallback: [false]
  });

  const alertColors = {
    success: {
      bgColor: "success.300",
      borderColor: "success.500"
    },
    error: {
      bgColor: "danger.300",
      borderColor: "danger.500"
    },
    warning: {
      bgColor: "warning.300",
      borderColor: "warning.500"
    },
    info: {
      bgColor: backgroundColor ?? "base.300",
      borderColor: alertBorderColor ?? "lines.400"
    }
  };

  const { bgColor, borderColor } = alertColors[type] || {};

  return (
    <Alert.Root
      data-bark-component="MessageBox"
      flexDirection={{ base: "column", md: "row" }}
      status={type}
      bg={bgColor}
      gap={4}
      p="24px"
      w="full"
      borderRadius={12}
      border="sm"
      borderStyle={isBorderLess ? "none" : "solid"}
      borderColor={borderColor}
      data-tracking-id={alertTrackingID}
      position="relative"
      role="alert"
    >
      <Flex gap={4} px={1} w="full" position="relative">
        <Flex alignItems="start" flex="1" direction="column">
          {title ? (
            <>
              <Flex gap={4} flexDirection="row" alignItems="start">
                <Box fontSize={24}>{messageIcons[type]()}</Box>
                <Alert.Title
                  textStyle="body.small"
                  color={textColor ?? "base.700"}
                  mb={1}
                  lineHeight={{ base: "1.43", md: "1.5" }}
                  fontSize={{ base: "14px", md: "16px" }}
                  fontWeight="semibold"
                  marginBottom={3}
                >
                  {title}
                </Alert.Title>
              </Flex>
              <Box marginLeft={10}>
                <Alert.Description>
                  <Text
                    textStyle="body.large"
                    color={textColor ?? "base.700"}
                    fontSize={{ base: "12px", md: "16px" }}
                    lineHeight={{ base: "1.67", md: "1.43" }}
                  >
                    {children}
                  </Text>
                </Alert.Description>
                {!isLargerThanMD && actionBtnText && (
                  <Button
                    variant="secondary"
                    alignSelf="flex-start"
                    flexShrink="0"
                    mt={4}
                    onClick={actionButtonAction}
                    size="sm"
                    {...rest}
                  >
                    {actionBtnText}
                  </Button>
                )}
              </Box>
            </>
          ) : (
            <Flex
              gap={4}
              flexDirection={{ base: "column", md: "row" }}
              alignItems={{ base: "flex-start", md: "center" }}
            >
              <Flex gap={4} alignItems="start">
                <Box fontSize={24}>{messageIcons[type]()}</Box>
                <Alert.Description>
                  <Text textStyle="body.large" color={textColor ?? "base.700"}>
                    {children}
                  </Text>
                </Alert.Description>
              </Flex>
              {!isLargerThanMD && actionBtnText && (
                <Button
                  variant="secondary"
                  alignSelf="flex-start"
                  flexShrink="0"
                  ml={{ base: 10, md: 0 }}
                  onClick={actionButtonAction}
                  size="sm"
                  {...rest}
                >
                  {actionBtnText}
                </Button>
              )}
            </Flex>
          )}
        </Flex>

        {isLargerThanMD && actionBtnText && (
          <Button
            variant="secondary"
            alignSelf="center"
            flexShrink="0"
            ml={4}
            onClick={actionButtonAction}
            size="sm"
            {...rest}
          >
            {actionBtnText}
          </Button>
        )}
      </Flex>
    </Alert.Root>
  );
};

export default MessageBox;
